import React, { useEffect } from 'react';

import { styled } from '@mui/material/styles';
import { useQuery } from '@tanstack/react-query';
import { Translate } from 'react-localize-redux';
import { connect } from 'react-redux';
import { useHistory } from 'react-router-dom';

import ErrorComponent from '@fd/ui/ErrorComponent';

import { getAccountDetails } from '../../../actions/account.actions';
import { notifyError } from '../../../layouts/Notify/actions';
import { accountRoutesConst } from '../../../routes/account.routes';
import {
  getFlipdishAuthorizationToken,
  getFlipdishAuthorizationTokenKey,
} from '../../../services/auth.service';
import {
  canUseIdTokenCookie,
  handleIdTokenLoginCallback,
  isAuth0IdTokenCutoverEnabled,
} from '../../../services/auth/idToken/idToken.service';
import { waitForProviderReady } from '../../../services/featureFlags/core/featureFlagClient';
import { PortalFeatureFlags } from '../../../services/featureFlags/portalFeatureFlags';
import Loading from '../../Loading';
import { executePostLoginRedirect } from './utils';

// ID token cutover: with the flag on, the SDK consumes the code itself (PKCE) and no FD
// cookie is minted. If the SDK path fails (e.g. the flag flipped between login start and
// callback, leaving no stored PKCE transaction), fall back to the classic exchange.
async function completeLogin(code: string, callbackState: string | null): Promise<unknown> {
  // Wait (bounded) for the Harness provider before choosing the path: a fresh callback
  // load races the provider's asynchronous init, and a pre-ready read fails safe to
  // off — an SDK-started login stores no fdauth0state, so an off read here would send
  // it into the classic state check below and bounce the user to /login. Resolves
  // immediately when the provider is already ready; on timeout the re-read below still
  // fails safe to the classic path.
  await waitForProviderReady({ provider: PortalFeatureFlags.auth0IdTokenCutover.provider });
  // Same gate as the login start: on origins where the ID-token cookie cannot
  // function (local development, unknown hosting) no SDK transaction exists, so
  // attempting the SDK callback would only fail with "Invalid state" before the
  // classic exchange runs anyway.
  if (isAuth0IdTokenCutoverEnabled() && canUseIdTokenCookie()) {
    try {
      await handleIdTokenLoginCallback();
      // TanStack Query v5 throws ("Query data cannot be undefined") when a queryFn
      // resolves undefined, which would fail the login right after a successful SDK
      // exchange — resolve with a sentinel instead. The classic path returns the
      // AxiosResponse, which is always defined.
      return true;
    } catch (error) {
      console.error('[IdToken] SDK callback failed; falling back to the classic exchange.', error);
    }
  }
  // The classic exchange grants a session to whoever presents the code — without the
  // classic state round trip it is a login-CSRF primitive (an attacker's code pasted
  // into a victim's callback URL would sign the victim in as the attacker). It is
  // enforced on every classic path, including the SDK-failure fallback: an SDK-started
  // login stores no fdauth0state, so a failed SDK callback cannot silently degrade into
  // an unchecked exchange.
  const sessionState = sessionStorage.getItem('fdauth0state');
  sessionStorage.removeItem('fdauth0state');
  if (!callbackState || !sessionState || callbackState !== sessionState) {
    throw new Error('Auth0 callback state mismatch');
  }
  return getFlipdishAuthorizationToken(code);
}

const StyledEmptyComponentContainer = styled('div')({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  height: '100vh',
});

type Props = MappedDispatch & MappedState;
const Auth0LoginCallback = ({
  auth0LoginError,
  dispatchNotifyError,
  dispatchGetAccountDetails,
}: Props) => {
  const history = useHistory();

  // Extract authorization code from the query string
  const queryString = window.location.search;
  const params = new URLSearchParams(queryString);
  const code = params.get('code');
  const callbackState = params.get('state');
  // Captured at render time, before any deferred work: completeLogin consumes (removes)
  // fdauth0state once its provider wait resolves, so the pre-flight below must compare
  // against the value present at mount rather than re-reading later — a post-consumption
  // read would misreport a successful classic login as a state mismatch. The effect's
  // [code] closure keeps this mount-time value for the guard's lifetime.
  const preflightSessionState = sessionStorage.getItem('fdauth0state');

  const getErrorTitle = () => {
    const error = params.get('error');
    const errorDescription = params.get('error_description');

    // Replace Auth0 error title with user-friendly messages
    if (error === 'access_denied') {
      if (errorDescription === 'Please verify your email before logging in.') {
        return <Translate id="Verify_email_address_title" />;
      }
    }

    return error;
  };

  const getErrorDescription = () => {
    const error = params.get('error');
    const errorDescription = params.get('error_description');

    // Replace Auth0 error descriptions with user-friendly messages
    if (error === 'access_denied') {
      if (errorDescription === 'Please verify your email before logging in.') {
        return <Translate id="Verify_email_address_description" />;
      }
    }

    return error;
  };

  const errorTitle = getErrorTitle();
  const errorDescription = getErrorDescription();

  const onError = (errormessage?: string) => {
    // If there is no error, push to login otherwise show the error instead of redirecting
    !errorTitle && history.push(accountRoutesConst.Login);
    dispatchNotifyError(errormessage);
  };

  useEffect(() => {
    // The classic state round trip is enforced inside completeLogin (before the exchange,
    // on every classic path). The pre-flight check below only preserves the immediate
    // redirect UX for a flag-off mismatch — it must not consume the stored state, which
    // completeLogin still validates and removes. It compares the mount-time capture
    // (preflightSessionState) instead of re-reading: completeLogin may already have
    // consumed the state by the time this deferred guard runs, and a re-read would
    // misreport a successful classic login as a mismatch. The error and missing-code
    // guards run in both flows — without them a code-less or failed redirect would sit
    // on the loading screen forever.
    //
    // All guards wait (bounded) for the Harness provider first: a fresh callback load
    // races the provider's asynchronous init, and a pre-ready flag read fails safe to
    // off — an SDK-started login stores no fdauth0state, so an off read would treat
    // that as a state error and bounce the user to /login. completeLogin re-checks the
    // flag under the same wait; the cancellation flag discards a result that arrives
    // after unmount.
    let cancelled = false;
    void waitForProviderReady({
      provider: PortalFeatureFlags.auth0IdTokenCutover.provider,
    }).then(() => {
      if (cancelled) {
        return;
      }
      if (!isAuth0IdTokenCutoverEnabled()) {
        if (!callbackState || !preflightSessionState || callbackState !== preflightSessionState) {
          console.log('state error');
          onError();
          return;
        }
      }

      if (errorTitle || errorDescription) {
        onError(errorTitle + ': ' + errorDescription);
        return;
      }

      if (!code) {
        console.log('Missing Authorization Code.');
        onError();
      }
    });

    return () => {
      cancelled = true;
    };
  }, [code]);

  const { isError, isSuccess } = useQuery({
    queryKey: [getFlipdishAuthorizationTokenKey, code],
    queryFn: () => completeLogin(code!, callbackState),
    enabled: !!code,
  });

  useEffect(() => {
    if (isSuccess) {
      dispatchGetAccountDetails();
      executePostLoginRedirect();
    } else if (isError) {
      dispatchNotifyError();
    }
  }, [isSuccess, isError]);

  useEffect(() => {
    if (auth0LoginError) {
      onError();
    }
  }, [auth0LoginError]);

  return errorTitle ? (
    <StyledEmptyComponentContainer>
      <ErrorComponent
        title={errorTitle as string}
        subtitle={errorDescription as string}
        shouldTranslate={false}
        action={() => history.push(accountRoutesConst.Logout)}
      />
    </StyledEmptyComponentContainer>
  ) : (
    <Loading fullscreen />
  );
};

type MappedState = ReturnType<ReturnType<typeof mapStateToProps>>;
const mapStateToProps = () => {
  return (state: AppState) => {
    const { account } = state;
    return {
      auth0LoginError: account.auth0LoginError,
    };
  };
};

type MappedDispatch = ReturnType<typeof mapDispatchToProps>;
const mapDispatchToProps = (dispatch: ThunkDispatch) => ({
  dispatchGetAccountDetails: () => dispatch(getAccountDetails()),
  dispatchNotifyError: (errormessage?: string) => {
    if (errormessage) {
      dispatch(notifyError({ message: errormessage, translate: false }));
    } else {
      dispatch(notifyError({ message: 'Something_went_wrong', translate: true }));
    }
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(Auth0LoginCallback);
